import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { AppIcons } from '../../../shared/app.icons';
import { AppSettings } from '../../../shared/app.settings';
import { MITableComponent } from '../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../shared/models/country';
import type { Language } from '../../../shared/models/language';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../shared/services/entity.service';

import { TranslateModule } from '@ngx-translate/core';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { forkJoin, Subscription } from 'rxjs';
import { ModuleHeaderComponent } from '../../../shared/components/module-header/module-header.component';
import { ConfigService } from '../../../shared/services/config.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { EntityCountsResponse, EntityList } from 'app/modules/shared/models/entity.models';
import { ListResponse } from '../../models/listResponse.model';
import { ChangeReviewDetailsComponent } from '../change-review-details/change-review-details.component';

@Component({
  selector: 'app-change-review-list',
  standalone: true,
  imports: [BreadcrumbModule, ModuleHeaderComponent, TabViewModule, TranslateModule, MITableComponent, MITableComponent, AccessProviderDirective, ChangeReviewDetailsComponent],
  templateUrl: './change-review-list.component.html',
  styleUrl: './change-review-list.component.scss'
})
export class ChangeReviewListComponent implements OnInit, OnDestroy {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;

  miIcons = AppIcons;
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns = [];
  cols = [];
  moduleName = 'change-review';
  filterList;
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels = {};
  filterAttributeLabels = {};
  listColumns: any;
  allColumns: any;
  pagination: any;
  totalRecords: number = 0;
  tableData: any = [];
  buttons: any[] = [];
  country: Country;
  language: Language;
  entityCount: EntityCountsResponse;
  itemsPerPage: number;
  uniqueId: string = 'change_review_grid';
  routePath: MenuItem[] = [];
  deleteProductMsg: string;
  deleteProductHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  filterFieldsArray: any[];
  isReady: boolean = false;
  attributeData: any;
  data;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  currentRow;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader;
  reviewHeader: any;
  btnUpdate: any;
  entityCreatedAtAttributeId;
  regionGeofences: any = [];
  zoneIds: any = [];
  isEntity = true;
  productStatusAttributeId: string;
  showImportExportBtn: boolean = false;
  entityData: EntityList;
  filteredItems;
  subscription: Subscription;

  constructor(private trialReviewService: EntityService,
    private cs: CommonBindingDataService, private confirmationService: ConfirmationService,
    private router: Router, private messageService: MessageService, private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService, private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef, private loaderService: LoaderService,
  ) { }



  ngOnInit() {
    this.initializeLabels();
    this.initializeColumn();
    this.initializeOtherVariablesAndAPI();
    this.getData();
  }

  initEntityPayload() {
    this.entityData = {
      limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT_TRIAL_REVIEW,
      offset: 0,
      searchStr: "",
      defaultSortColumn: "updatedAt",
      defaultSortType: "desc",
      eventCode: "",
      forTenantCode: this.configService.getForTenantCode(),
      protocolIds: [],
      status: [],
    };
  }

  initializeLabels() {
    this.routePath = [{ label: this.cs.getLabel('change_review.lbl_change_reviews'), routerLink: 'change-review/list' }];
  }

  initializeColumn() {
    this.allColumns = [
      {
        "attributeCode": "Id",
        "attributeIndex": 0,
        "field": "Id",
        "header": this.cs.getLabel('change_review.lbl_list_id'),
        "isSortable": true
      },
      {
        "attributeCode": "protocol",
        "attributeIndex": 1,
        "field": "protocol",
        "header": this.cs.getLabel('change_review.lbl_protocol'),
      },
      {
        "attributeCode": "change",
        "attributeIndex": 2,
        "field": "change",
        "header": this.cs.getLabel('change_review.lbl_change'),
      },
      {
        "attributeCode": "data",
        "attributeIndex": 3,
        "field": "data",
        "header": this.cs.getLabel('change_review.lbl_data'),
      },
      {
        "attributeCode": "changeReason",
        "attributeIndex": 4,
        "field": "changeReason",
        "header": this.cs.getLabel('change_review.lbl_protocol_reason'),
      },
      {
        "attributeCode": "changeReviewStatus",
        "attributeIndex": 5,
        "field": "changeReviewStatus",
        "header": this.cs.getLabel('change_review.lbl_status'),
        "isSortable": true
      },
    ];
  }

  initializeOtherVariablesAndAPI() {
    this.btnLabel = this.cs.getLabel('change_review.lbl_change_reviews');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.initEntityPayload();
  }

  public onChange(event) {
    for (const file of event.target.files) {
      if (file) {
        if (file.size > AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: this.cs.getLabel('err_file_size_exceed_msg') });
        } else {
          const formData = new FormData();
          formData.append("file", file);
          formData.append('forTenantCode', this.configService.getForTenantCode());
          formData.append('entityCode', AppSettings.ENTITY_CODE.CHANGE_REVIEW);
          formData.append('viewCode', AppSettings.VIEW_CODE.EXCEL_IMPORT_VIEW);
          this.cs.importEntity(formData, AppSettings.ENTITY_CODE.CHANGE_REVIEW).subscribe(() => {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('lbl_file_uploaded_successfully') });
          })
        }
      }
    }
  }

  update() {
    this.onUpdateReviewStatus(this.currentRow.protocolReviewRequestId);
  }

  onUpdateReviewStatus(protocolReviewRequestId) {
    this.router.navigate([`app/change-review/change-review-details/${protocolReviewRequestId}`]);
  }

  onRowSelect(event) {
    if (event.type === 'row') {
      this.router.navigate([`app/change-review/change-review-details/${event.data.protocolReviewRequestId}`,]);
      this.trialReviewService.entityCurrentRow.next(event.data);
    }
  }

  getData() {
    return forkJoin({
      tableViewData: this.getTableView(),
      filterViewDate: this.getFilterView(),
    })
  }

  getTableView() {
    this.isLoading = false;
    this.searchEntity();
    this.filterAvailableColumns = _.clone(this.allColumns);
    this.isEntity = false;
  }

  onSortData(sortColumnDetails) {
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  searchEntity() {
    this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;
    this.loaderService.hide();
    this.trialReviewService.searchEntityChangeReview(AppSettings.ENTITY_CODE.CHANGE_REVIEW, this.entityData, false).subscribe((res: ListResponse) => {
      this.listColumns = res?.data[0]?.values;
      this.pagination = res?.pagination;
      this.totalRecords = res?.count;
      this.tableData = res.data.map(data => ({
        protocol: `${data.protocolName}`,
        Id: `${data.protocolReviewRequestId}`,
        changeReviewStatus: `${data.status}`,
        change: `${data.action} - ${data.model}`,
        ...data
      }));
      this.loaderService.hide();
    })
  }


  getFilterView() {
    this.filterList = [
      {
        "attributeCode": "changeReviewStatus",
        "attributeIndex": 0,
        "presetValues": [
          {
            "labelKey": "Pending",
            "labelValue": "Pending",
          },
          {
            "labelKey": "Approved",
            "labelValue": "Approved",
          },
          {
            "labelKey": "Rejected",
            "labelValue": "Rejected",
          }
        ],
      },
      {
        "attributeCode": "protocol",
        "attributeIndex": 1,
        "inputCode": "autoCompleteSearch"
      }
    ];
  }

  onSearchValueChanges(event) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  onFilterValueChange(event) {

    const selectedStatuses = event.changeReviewStatus || [];


    this.initEntityPayload();
    this.entityData.filters = [];

    if (selectedStatuses.length > 0) {
      this.entityData.status = selectedStatuses.map(status => status);
    } else {
      this.entityData.status = [];
    }
    if (event.protocol) {
      this.entityData.protocolIds = [event.protocol];
    } else {
      delete this.entityData.protocolIds;
    }

    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');

    this.searchEntity();
  }

  onPageChange(event) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  getLabel(params) {
    return `${params}hi`;
  }

  onRowClicked(e: any) {
    this.messageService.add({ severity: 'info', summary: 'Info', detail: `Row clicked: ${JSON.stringify(e)}` });
  }

  onActionBtnClicked(e: any) {
    this.messageService.add({ severity: 'info', summary: 'Info', detail: `Action button clicked: ${JSON.stringify(e)}` });
  }

  ngOnDestroy() {
    this.searchEntity();
    this.getFilterView();
  }

}
